import { useState, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ChevronLeft, ChevronRight, Plane, Clock } from 'lucide-react';

const statusColors = {
    scheduled: 'bg-slate-500/20 border-l-slate-400',
    confirmed: 'bg-blue-500/20 border-l-blue-400',
    preparing: 'bg-cyan-500/20 border-l-cyan-400',
    boarding: 'bg-amber-500/20 border-l-amber-400',
    closed: 'bg-orange-500/20 border-l-orange-400',
    departed: 'bg-emerald-500/20 border-l-emerald-400',
    en_route: 'bg-green-500/20 border-l-green-400',
    arrived: 'bg-emerald-600/20 border-l-emerald-500',
    delayed: 'bg-yellow-500/20 border-l-yellow-400',
    cancelled: 'bg-red-500/20 border-l-red-400',
};

const statusLabels = {
    scheduled: 'PROG', confirmed: 'CONF', preparing: 'PREP', boarding: 'ABRD',
    closed: 'CERR', departed: 'DESP', en_route: 'VUELO', arrived: 'LLEG',
    delayed: 'DEM', cancelled: 'CANC',
};

const DAYS_ES = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export const FlightCalendarView = ({ flights, onFlightClick }) => {
    const [currentDate, setCurrentDate] = useState(new Date());

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const calendarDays = useMemo(() => {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        let startDow = firstDay.getDay() - 1;
        if (startDow < 0) startDow = 6;

        const days = [];
        // Previous month padding
        for (let i = startDow - 1; i >= 0; i--) {
            const d = new Date(year, month, -i);
            days.push({ date: d, isCurrentMonth: false });
        }
        // Current month
        for (let d = 1; d <= lastDay.getDate(); d++) {
            days.push({ date: new Date(year, month, d), isCurrentMonth: true });
        }
        // Next month padding
        const remaining = 7 - (days.length % 7);
        if (remaining < 7) {
            for (let d = 1; d <= remaining; d++) {
                days.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
            }
        }
        return days;
    }, [year, month]);

    const flightsByDate = useMemo(() => {
        const map = {};
        flights.forEach(f => {
            const dateStr = f.scheduled_departure?.split('T')[0];
            if (!dateStr) return;
            if (!map[dateStr]) map[dateStr] = [];
            map[dateStr].push(f);
        });
        // Sort flights within each day by departure time
        Object.keys(map).forEach(k => {
            map[k].sort((a, b) => (a.scheduled_departure || '').localeCompare(b.scheduled_departure || ''));
        });
        return map;
    }, [flights]);

    const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
    const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
    const goToday = () => setCurrentDate(new Date());

    const todayStr = new Date().toISOString().split('T')[0];

    const formatDateKey = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    return (
        <Card className="card-aviation" data-testid="flight-calendar-view">
            <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="cal-prev-month">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <h2 className="text-xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight min-w-[200px] text-center" data-testid="cal-month-label">
                            {MONTHS_ES[month]} {year}
                        </h2>
                        <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="cal-next-month">
                            <ChevronRight className="w-5 h-5" />
                        </Button>
                    </div>
                    <Button variant="outline" size="sm" onClick={goToday} data-testid="cal-today-btn">
                        Hoy
                    </Button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 gap-px mb-1">
                    {DAYS_ES.map(d => (
                        <div key={d} className="text-center text-xs font-bold uppercase tracking-widest text-muted-foreground py-2">
                            {d}
                        </div>
                    ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-px bg-border/30 rounded-lg overflow-hidden">
                    {calendarDays.map((day, idx) => {
                        const dateKey = formatDateKey(day.date);
                        const dayFlights = flightsByDate[dateKey] || [];
                        const isToday = dateKey === todayStr;
                        const maxShow = 3;

                        return (
                            <div
                                key={idx}
                                className={`min-h-[100px] p-1.5 transition-colors ${
                                    day.isCurrentMonth ? 'bg-card' : 'bg-muted/20'
                                } ${isToday ? 'ring-2 ring-primary/50 ring-inset' : ''}`}
                                data-testid={`cal-day-${dateKey}`}
                            >
                                <div className={`text-xs font-mono mb-1 ${
                                    isToday ? 'text-primary font-bold' : day.isCurrentMonth ? 'text-foreground' : 'text-muted-foreground/50'
                                }`}>
                                    {day.date.getDate()}
                                </div>
                                <div className="space-y-0.5">
                                    {dayFlights.slice(0, maxShow).map(f => (
                                        <button
                                            key={f.id}
                                            onClick={() => onFlightClick?.(f)}
                                            className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] border-l-2 truncate transition-opacity hover:opacity-80 cursor-pointer ${statusColors[f.status] || statusColors.scheduled}`}
                                            data-testid={`cal-flight-${f.id}`}
                                        >
                                            <span className="font-bold font-mono">{f.flight_number}</span>
                                            <span className="ml-1 text-muted-foreground">{f.origin}-{f.destination}</span>
                                        </button>
                                    ))}
                                    {dayFlights.length > maxShow && (
                                        <div className="text-[10px] text-primary font-medium pl-1">
                                            +{dayFlights.length - maxShow} mas
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-border/30">
                    {Object.entries(statusLabels).map(([key, label]) => (
                        <div key={key} className="flex items-center gap-1.5 text-[10px]">
                            <div className={`w-2.5 h-2.5 rounded-sm border-l-2 ${statusColors[key]}`} />
                            <span className="text-muted-foreground">{label}</span>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};
